// wizardCompletion — pure logic for "which wizard steps should be marked
// complete after hydrating data for an existing org?"
//
// Pulled out of WizardContext.tsx so it can be unit-tested without the
// RTK Query / async machinery that surrounds the real hydration path.
// The hydrator collects entity counts from the API, hands them to
// computeHydratedCompletedSteps, and stores the returned list as
// state.completedSteps.
//
// The completion model has three tiers (step numbers reflect the current
// WizardStep enum, i.e. AFTER ChannelModifiers was inserted at index 5 —
// see the WizardStep doc comment in wizardInvalidation.ts):
//
//   - REQUIRED (steps 0–4 and 6: Org, Server, Adapters, Channels, Banks,
//     Lifts) — these gate the wizard; they must have actual data
//     present to be marked complete. Otherwise the user could
//     prematurely click "Complete Setup" on a half-configured
//     install.
//
//   - HALF-REQUIRED (step 9: Users & Roles) — at least one role or
//     one user must exist. The bootstrap path always seeds the
//     master admin role + admin user, so for properly-bootstrapped
//     orgs this clears automatically.
//
//   - OPTIONAL (step 5 Channel Modifiers, 7 Debounce, 8 BACnet, 10
//     Notifications, 11 Actions, 12 Monitor Grid) — Debounce/BACnet/
//     Notifications/Actions/Monitor Grid auto-complete as soon as their
//     prerequisite is complete, regardless of whether the user
//     configured anything inside them; Channel Modifiers is the one
//     exception (see below) and only completes when the operator saves
//     one. The wizard does not block "Complete Setup" on these being
//     non-empty, so the step indicator should not show them grey just
//     because the operator chose to skip them. Re-marking them
//     incomplete after a genuine upstream change is the job of the
//     smart-invalidation rules in wizardInvalidation.ts; this hydration
//     helper only decides the initial post-load state.

import { channelAddressFacts, channelHasValidAddress, ChannelRecord } from "./channelAddress";
import { WizardStep } from "./wizardInvalidation";

/**
 * Channel shape needed to decide whether the Channels step is complete: a
 * channel record as the channels list returns it, in either the FlatBuffer
 * camelCase spelling or the legacy snake_case JSON one. See `ChannelRecord`.
 */
export type ChannelCompletionInput = ChannelRecord;

export interface CompletionInputs {
  serverId: string | null | undefined;
  adapterIds: readonly string[];
  channelIds: readonly string[];
  channelItems: readonly ChannelCompletionInput[];
  bankIds: readonly string[];
  liftIdsByBank: Readonly<Record<string, readonly string[]>>;
  roleIds: readonly string[];
  userIds: readonly string[];
  /** When "ACTIVE", the org has passed Review — all 14 steps are verified. */
  orgStatus?: "DRAFT" | "ACTIVE" | null;
}

/**
 * Returns the sorted list of wizard steps that should be marked
 * complete given the entity counts hydrated from the API.
 *
 * Step 0 (Organization) is always included — the helper is only
 * called when an orgId is known. Subsequent required steps are
 * pushed when their backing data is present. Optional steps cascade
 * via prerequisite chains: Debounce/BACnet after Lifts, then
 * Notifications/Actions/MonitorGrid after Users & Roles.
 */
export function computeHydratedCompletedSteps(input: CompletionInputs): number[] {
  // An ACTIVE org has already passed Review — every step is verified.
  // Derive from WizardStep so a newly-added step is included automatically.
  if (input.orgStatus === "ACTIVE") {
    return Object.values(WizardStep);
  }

  const completed: number[] = [];

  // Step 0: Organization — always complete when we're hydrating.
  completed.push(WizardStep.Organization);

  // Step 1: Server — required, presence-based.
  if (input.serverId) completed.push(WizardStep.Server);

  // Step 2: Adapters — required, presence-based.
  if (input.adapterIds.length > 0) completed.push(WizardStep.Adapters);

  // Step 3: Channels — required, ALL channels need a usable address. This is
  // the same predicate StepChannels' `allChannelsValid` applies, so a resumed
  // org and a live session agree: a SINGLE channel keeps its address in
  // vendor_socket_address and a ROUTABLE one in routable_socket_address, so
  // either column counts; serial channels are exempt (device path, not
  // socket); and a TCP channel with a valid opc.tcp:// endpoint is addressed
  // without a socket.
  const allChannelsHaveAddress =
    input.channelIds.length > 0 &&
    input.channelItems.every((ch) => channelHasValidAddress(channelAddressFacts(ch)));
  if (allChannelsHaveAddress) completed.push(WizardStep.Channels);

  // Step 4: Banks — required, presence-based.
  if (input.bankIds.length > 0) completed.push(WizardStep.Banks);

  // Step 5: Channel Modifiers — optional, **NOT auto-completed** by this
  // helper. The step is marked complete only when the operator saves at
  // least one modifier (via `markStepCompleted(WizardStep.ChannelModifiers)`
  // in `StepChannelModifiers.handleSave`). Auto-completing it would make
  // the Review section's "Configured" badge always show, even for orgs
  // that intentionally have no modifiers — losing the signal of whether
  // any modifier was actually saved.

  // Step 6: Lifts & Floors — required, presence-based (at least one
  // bank must have at least one lift).
  if (Object.keys(input.liftIdsByBank).length > 0) completed.push(WizardStep.Lifts);

  // Step 7: Debounce — optional, auto-complete once Lifts cleared.
  if (completed.includes(WizardStep.Lifts)) completed.push(WizardStep.Debounce);

  // Step 8: BACnet — optional, auto-complete once Debounce cleared.
  if (completed.includes(WizardStep.Debounce)) completed.push(WizardStep.Bacnet);

  // Step 9: Users & Roles — required, presence-based. The bootstrap
  // path always seeds at least the master admin role + admin user,
  // so for properly-bootstrapped orgs this clears automatically.
  if (input.roleIds.length > 0 || input.userIds.length > 0) {
    completed.push(WizardStep.Users);
  }

  // Steps 10-12: Notifications, Actions, Monitor Grid — optional,
  // auto-complete once Users & Roles cleared. Mirrors the
  // Debounce/BACnet pattern. Field engineers opening a verified org
  // for review should see all optional steps green by default; the
  // smart-invalidation rules pick up genuine structural changes
  // when the operator edits something that actually affects them.
  if (completed.includes(WizardStep.Users)) completed.push(WizardStep.Notifications);
  if (completed.includes(WizardStep.Notifications)) completed.push(WizardStep.Actions);
  if (completed.includes(WizardStep.Actions)) completed.push(WizardStep.MonitorGrid);

  return completed;
}

/** Find the first step in [0..maxStep] that is not in completedSteps. */
export function firstIncompleteStep(completedSteps: readonly number[], maxStep: number): number {
  for (let i = 0; i <= maxStep; i += 1) {
    if (!completedSteps.includes(i)) return i;
  }
  return maxStep;
}

/**
 * Find the first step in the half-open range [0, beforeStep) that is not yet complete — the
 * step an operator would silently skip past by jumping straight to `beforeStep`.
 *
 * Returns `null` when the range is gapless, meaning every step before `beforeStep` already
 * reads complete and the jump is equivalent to pressing Next repeatedly. The completion state
 * of `beforeStep` itself is deliberately not considered: the operator is navigating *to* that
 * step in order to work on it, not past it.
 *
 * Completion is supplied as a predicate rather than a `completedSteps` collection so callers
 * pass the exact same completion oracle they already use elsewhere (e.g. `useWizard()`'s
 * `isStepCompleted`), rather than this module assuming a particular storage shape for it. It is
 * deliberately generic and carries no wizard-specific knowledge (e.g. which steps are optional
 * and should be exempt from a gap check) — that judgment belongs at the call site, the same way
 * `filterEmptyOptionalSteps` and `computeStepVisual` each apply it at their own point of use
 * rather than inside a shared primitive like this one.
 *
 * @param beforeStep Exclusive upper bound — the step index being navigated to.
 * @param isStepCompleted Returns true when the given step index reads complete.
 * @returns The lowest incomplete step index below `beforeStep`, or `null` when there is none.
 */
export function firstGapBefore(
  beforeStep: number,
  isStepCompleted: (step: number) => boolean,
): number | null {
  for (let i = 0; i < beforeStep; i += 1) {
    if (!isStepCompleted(i)) {
      return i;
    }
  }
  return null;
}
